"use client"

import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import { m, AnimatePresence } from "framer-motion"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { MockupDate } from "@/lib/mockup-date"
import { cn } from "@/lib/utils"
import { ClientOnly } from "@/lib/animation/client-only"
import { howItWorksKeys as k } from "@/i18n/how-it-works-keys"
import { PersonPackageIcon } from "@/components/ui/package-icons"
import { blobMediaUrl } from "@/lib/blob-url"

/* ───────────────────── Phone walkthrough steps ───────────────────── */

const PHONE_STEPS = [
  {
    number: "01",
    labelKey: "step1Label" as const,
    titleKey: "step1Title" as const,
    descKey: "step1Desc" as const,
  },
  {
    number: "02",
    labelKey: "step2Label" as const,
    titleKey: "step2Title" as const,
    descKey: "step2Desc" as const,
  },
  {
    number: "03",
    labelKey: "step3Label" as const,
    titleKey: "step3Title" as const,
    descKey: "step3Desc" as const,
  },
  {
    number: "04",
    labelKey: "step4Label" as const,
    titleKey: "step4Title" as const,
    descKey: "step4Desc" as const,
  },
]

/* ───────────────────── iPhone frame component ───────────────────── */

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative mx-auto w-[280px] md:w-[320px]">
      <div className="relative overflow-hidden rounded-[44px] border border-neutral-100 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
        {/* Dynamic Island */}
        <div className="flex justify-center pt-4 pb-2">
          <div className="h-[24px] w-[80px] rounded-full bg-neutral-900" />
        </div>

        {/* Fixed height screen area */}
        <div className="relative h-[520px] overflow-hidden">{children}</div>

        {/* Home indicator */}
        <div className="flex justify-center pt-2 pb-4">
          <div className="h-[4px] w-[110px] rounded-full bg-neutral-200" />
        </div>
      </div>
    </div>
  )
}

/* ───────────────────── Phone screen content components ───────────────────── */

/* Screen 1: Scheduler — "Book your first scan" */
function SchedulerScreen({ bloodOnly }: { bloodOnly: boolean }) {
  const mt = useTranslations("howItWorks")
  const days = [
    [null, null, null, null, null, 1, 2],
    [3, 4, 5, 6, 7, 8, 9],
    [10, 11, 12, 13, 14, 15, 16],
    [17, 18, 19, 20, 21, 22, 23],
    [24, 25, 26, 27, 28, null, null],
  ]
  const available = [4, 5, 7, 10, 12, 13, 14, 18, 20, 24, 26, 28]

  return (
    <div className="px-5 py-4">
      <p className="type-title-sm text-neutral-400">{mt("mockupGreeting")}</p>
      <h3 className="type-title-lg text-aubergine-900 mt-1 leading-tight font-normal">
        {mt("mockupBookScan")}
      </h3>

      {/* What / Where fields */}
      <div className="mt-5 space-y-2">
        <div className="flex items-center justify-between rounded-full border border-neutral-200 px-4 py-2.5">
          <span className="type-label-sm text-neutral-400">
            {mt("mockupWhat")}
          </span>
          <span className="type-label-sm text-aubergine-900 font-medium">
            {mt(bloodOnly ? "mockupBloodTest" : "mockupFullBodyMri")}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-full border border-neutral-200 px-4 py-2.5">
          <span className="type-label-sm text-neutral-400">
            {mt("mockupWhere")}
          </span>
          <span className="type-label-sm text-aubergine-900 font-medium">
            {mt("mockupClinic")}
          </span>
        </div>
      </div>

      {/* Calendar */}
      <div className="mt-4 rounded-2xl border border-neutral-100 p-3">
        <p className="type-label-md text-aubergine-900 mb-1 font-medium">
          {mt("mockupWhenScan")}
        </p>
        <div className="border-t border-neutral-100 pt-2">
          <p className="type-label-sm mb-2 font-medium text-neutral-700">
            {mt("mockupCalendarMonth")}
          </p>
          <div className="type-label-sm grid grid-cols-7 gap-y-1 text-center">
            {(
              [
                "mockupMo",
                "mockupTu",
                "mockupWe",
                "mockupTh",
                "mockupFr",
                "mockupSa",
                "mockupSu",
              ] as const
            ).map((d) => (
              <span key={d} className="py-1 font-medium text-neutral-400">
                {mt(d)}
              </span>
            ))}
            {days.flat().map((day, i) => {
              if (!day) return <span key={i} />
              const isSelected = day === 4
              const isAvail = available.includes(day)
              return (
                <span
                  key={i}
                  className={cn(
                    "type-label-sm relative rounded-full py-1",
                    isSelected
                      ? "bg-[#b8c0d8] font-medium text-white"
                      : isAvail
                        ? "text-aubergine-900 font-medium"
                        : "text-neutral-300"
                  )}
                >
                  {day}
                  {isAvail && !isSelected && (
                    <span className="absolute -bottom-0.5 left-1/2 h-[3px] w-[3px] -translate-x-1/2 rounded-full bg-[#6E8CD2]" />
                  )}
                </span>
              )
            })}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <div className="type-label-sm flex-1 rounded-full border border-neutral-200 px-3 py-1.5 font-mono text-neutral-500">
            {mt("mockupTime")}
          </div>
          <div className="type-label-sm flex-1 rounded-full bg-[#b8c0d8] px-3 py-1.5 text-center font-mono font-medium tracking-wider text-white">
            {mt("mockupSelectTime")}
          </div>
        </div>
      </div>
    </div>
  )
}

/* Screen 2: Confirmation — "Your first scan will be soon!" */
function ConfirmationScreen({ bloodOnly }: { bloodOnly: boolean }) {
  const mt = useTranslations("howItWorks")
  return (
    <div className="flex h-full flex-col items-center justify-center px-5 py-4">
      {/* Check circle */}
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#b8c0d8]">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 12l4 4 8-8"
            stroke="#371938"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <h3 className="type-title-lg text-aubergine-900 text-center leading-tight font-normal">
        {mt("mockupScanSoon")}
      </h3>
      <p className="type-body-md mt-2 text-neutral-500">
        {mt("mockupConfirmDate")}
      </p>

      {/* Test cards */}
      <div className="mt-6 w-full space-y-2">
        {!bloodOnly && (
          <div className="flex items-center gap-3 rounded-2xl bg-neutral-50 p-3">
            <div className="bg-light-green-200 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[8px]">
              <PersonPackageIcon size={26} />
            </div>
            <div>
              <p className="type-body-md text-aubergine-900 font-medium">
                {mt("mockupFullBodyMri")}
              </p>
              <p className="type-body-sm text-neutral-400">60 min</p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 rounded-2xl bg-neutral-50 p-3">
          <Image
            src={blobMediaUrl("/images/homepage/service-blood-cells.jpeg")}
            alt=""
            width={88}
            height={88}
            className="h-11 w-11 flex-shrink-0 rounded-xl object-cover"
          />
          <div>
            <p className="type-body-md text-aubergine-900 font-medium">
              {mt("mockupBloodTest")}
            </p>
            <p className="type-body-sm text-neutral-400">10 min</p>
          </div>
        </div>
      </div>

      <p className="type-body-sm mt-auto pt-6 text-neutral-500 underline underline-offset-2">
        {mt("mockupPrepare")}
      </p>
    </div>
  )
}

/* Screen 3: Action plan / Timeline */
function TimelineScreen() {
  const mt = useTranslations("howItWorks")
  return (
    <div className="px-5 py-4">
      <p className="type-body-md text-neutral-400">{mt("mockupGreeting")}</p>
      <h3 className="type-body-xl text-aubergine-900 mt-1 leading-snug font-normal">
        {mt("mockupStrongStart")}
      </h3>

      {/* Tabs */}
      <div className="type-body-sm mt-4 flex gap-4 border-b border-neutral-100">
        <span className="text-aubergine-900 border-aubergine-900 border-b-2 pb-2 font-medium">
          {mt("mockupTabTimeline")}
        </span>
        <span className="pb-2 text-neutral-400">{mt("mockupTabResults")}</span>
        <span className="pb-2 text-neutral-400">
          {mt("mockupTabActionPlan")}
        </span>
        <span className="pb-2 text-neutral-400">
          {mt("mockupTabDocuments")}
        </span>
      </div>

      {/* Timeline */}
      <div className="relative mt-4 pl-6">
        {/* Vertical line */}
        <div className="absolute top-2 bottom-2 left-[7px] w-px bg-neutral-200" />

        {/* February */}
        <div className="relative mb-5">
          <div className="absolute top-0.5 -left-6 flex h-4 w-4 items-center justify-center rounded-full bg-[#6E8CD2]">
            <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
              <path
                d="M3 6l2 2 4-4"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="type-label-md text-aubergine-900 mb-2 font-medium">
            {mt("mockupMonthFeb")}
          </p>
          <div className="flex items-center gap-3 rounded-2xl bg-neutral-50 p-2.5">
            <Image
              src={blobMediaUrl("/images/mega-menu/how-it-works.jpg")}
              alt=""
              width={80}
              height={80}
              className="h-10 w-10 flex-shrink-0 rounded-xl object-cover"
            />
            <div className="flex-1">
              <p className="type-label-sm text-aubergine-900 font-medium">
                {mt("mockupFirstScan")}
              </p>
              <p className="type-body-sm text-neutral-400">
                {mt("mockupSeeResults")}
              </p>
            </div>
            <span className="text-sm text-neutral-300">&rsaquo;</span>
          </div>
        </div>

        {/* March */}
        <div className="relative mb-5">
          <div className="absolute top-0.5 -left-6 h-4 w-4 rounded-full border-2 border-[#6E8CD2] bg-white" />
          <p className="type-label-md text-aubergine-900 mb-2 font-medium">
            {mt("mockupMonthMar")}
          </p>
          <div className="flex items-center gap-3 rounded-2xl bg-neutral-50 p-2.5">
            <Image
              src={blobMediaUrl("/images/report/hirslanden-zurich.png")}
              alt=""
              width={80}
              height={80}
              className="h-10 w-10 flex-shrink-0 rounded-xl object-cover"
            />
            <div className="flex-1">
              <p className="type-label-sm text-aubergine-900 font-medium">
                {mt("mockupCardiologist")}
              </p>
              <p className="type-body-sm text-neutral-400">
                {mt("mockupSeeAppointment")}
              </p>
            </div>
            <span className="text-sm text-neutral-300">&rsaquo;</span>
          </div>
        </div>

        {/* August */}
        <div className="relative">
          <div className="absolute top-0.5 -left-6 h-4 w-4 rounded-full border-2 border-neutral-200 bg-white" />
          <p className="type-label-md mb-2 font-medium text-neutral-400">
            {mt("mockupMonthAug")}
          </p>
          <div className="flex items-center gap-3 rounded-2xl bg-neutral-50 p-2.5">
            <Image
              src={blobMediaUrl("/images/report/recs/followup-blood-test.jpg")}
              alt=""
              width={80}
              height={80}
              className="h-10 w-10 flex-shrink-0 rounded-xl object-cover"
            />
            <div className="flex-1">
              <p className="type-label-sm font-medium text-neutral-500">
                {mt("mockupRetestHormones")}
              </p>
              <p className="type-body-sm text-neutral-300">
                {mt("mockupSchedule")}
              </p>
            </div>
            <span className="text-sm text-neutral-300">&rsaquo;</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/* Screen 4: Re-test / Compare — biomarker cards with trend */
function CompareScreen() {
  const mt = useTranslations("howItWorks")
  const locale = useLocale()
  return (
    <div className="px-5 py-4">
      <p className="type-body-md text-neutral-400">{mt("mockupGreeting")}</p>
      <h3 className="type-title-lg text-aubergine-900 mt-1 leading-tight font-normal">
        {mt("mockupReview")}
      </h3>

      {/* Scan tabs */}
      <div className="type-body-sm mt-4 flex items-center gap-3">
        <span className="text-aubergine-900 rounded-full bg-neutral-100 px-3 py-1.5 font-medium">
          {mt("mockupOverview")}
        </span>
        <span className="text-neutral-400">{mt("mockupScan1")}</span>
        <span className="text-neutral-400">{mt("mockupScan2")}</span>
        <span className="text-neutral-400">{mt("mockupScan3")}</span>
      </div>

      {/* Biomarker card 1 */}
      <div className="mt-4 rounded-2xl border border-neutral-100 p-3.5">
        <p className="type-label-md text-aubergine-900 font-medium">
          {mt("mockupHdl")}
        </p>
        <div className="mt-1 flex items-end justify-between">
          <div>
            {/* Kept inside the healthy HDL band: above ~2.0 mmol/L is linked
                to higher mortality, so a "good" 3.2 misleads. */}
            <span className="type-headline-lg text-aubergine-900 leading-none font-normal">
              1.6
            </span>
            <span className="type-body-sm ml-1 font-mono text-neutral-400">
              MMOL/L
            </span>
          </div>
          {/* Mini trend line */}
          <svg width="80" height="30" viewBox="0 0 80 30" fill="none">
            <path
              d="M5 25 L30 20 L55 18 L75 8"
              stroke="#b5b5b5"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
            <circle cx="30" cy="20" r="3" fill="#b5b5b5" />
            <circle cx="55" cy="18" r="3" fill="#b5b5b5" />
            <circle
              cx="75"
              cy="8"
              r="4"
              fill="white"
              stroke="#6E8CD2"
              strokeWidth="2"
            />
          </svg>
        </div>
        <div className="type-body-sm mt-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-neutral-500">
              {mt("mockupScan3")}
            </span>
            <span className="font-mono text-neutral-700">1.6 MMOL/L</span>
            <span className="bg-deep-green-100 text-deep-green-600 rounded px-2 py-0.5 font-medium">
              {mt("mockupGood")}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-neutral-500">
              {mt("mockupScan2")}
            </span>
            <span className="font-mono text-neutral-700">1.3 MMOL/L</span>
            <span className="bg-deep-green-100 text-deep-green-600 rounded px-2 py-0.5 font-medium">
              {mt("mockupGood")}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-neutral-500">
              {mt("mockupScan1")}
            </span>
            <span className="font-mono text-neutral-700">0.8 MMOL/L</span>
            <span className="bg-error-100 text-error-700 rounded px-2 py-0.5 font-medium">
              {mt("mockupLow")}
            </span>
          </div>
        </div>
      </div>

      {/* Detailed report link */}
      <div className="mt-2 flex items-center justify-between px-1 py-2">
        <div>
          <p className="type-label-md text-aubergine-900 font-medium">
            {mt("mockupDetailedReport")}
          </p>
          <p className="type-body-sm text-neutral-400">
            <MockupDate locale={locale} />
          </p>
        </div>
        <span className="text-neutral-300">&rsaquo;</span>
      </div>
    </div>
  )
}

/* ───────────────────── Phone walkthrough component ───────────────────── */

/**
 * `bloodOnly` strips the scan from the mockups: the scheduler books the blood
 * test rather than a full-body MRI, and the appointment card loses its MRI
 * row. Opt-in per caller rather than derived from `country`, because the DE
 * ad LPs that also mount this (/de/de/go/fullbodymri, /de/de/go/checkup)
 * pitch their own offer and must keep the scan.
 */
export function PhoneWalkthrough({
  bloodOnly = false,
}: {
  bloodOnly?: boolean
}) {
  const t = useTranslations("howItWorks")
  const ut = useTranslations("ui")
  const keys = k
  const [activeStep, setActiveStep] = useState(0)
  const [progress, setProgress] = useState(0)
  const [trackBounds, setTrackBounds] = useState({ top: 0, height: 0 })
  const sectionRef = useRef<HTMLDivElement>(null)
  const stepsColumnRef = useRef<HTMLDivElement>(null)
  const dotsRef = useRef<(HTMLDivElement | null)[]>([])
  // The two AnimatePresence regions below (phone-screen swap, scroll
  // hint) are mount-gated via <ClientOnly> from lib/animation/client-only —
  // framer-motion's Math.random() / useId() would otherwise force this
  // subtree out of the static prerender into a per-request stream
  // (ppr-deferred). The ClientOnly fallback ships a plain SSR-friendly
  // rendering of the current step screen; the scroll-hint cue is
  // decorative and renders nothing on the server.

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return

    const handleScroll = () => {
      const rect = section.getBoundingClientRect()
      const scrollRange = section.offsetHeight - window.innerHeight
      if (scrollRange <= 0) return
      const p = Math.max(0, Math.min(1, -rect.top / scrollRange))
      setProgress(p)
      setActiveStep(Math.min(3, Math.floor(p * 4)))
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const measure = () => {
      const column = stepsColumnRef.current
      const firstDot = dotsRef.current[0]
      const lastDot = dotsRef.current[PHONE_STEPS.length - 1]
      if (!column || !firstDot || !lastDot) return
      const columnRect = column.getBoundingClientRect()
      const firstRect = firstDot.getBoundingClientRect()
      const lastRect = lastDot.getBoundingClientRect()
      const firstCenter = firstRect.top + firstRect.height / 2 - columnRect.top
      const lastCenter = lastRect.top + lastRect.height / 2 - columnRect.top
      setTrackBounds({
        top: firstCenter,
        height: Math.max(0, lastCenter - firstCenter),
      })
    }
    measure()
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null
    if (ro && stepsColumnRef.current) ro.observe(stepsColumnRef.current)
    window.addEventListener("resize", measure)
    return () => {
      ro?.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [])

  return (
    <>
      {/* ─── Mobile: stacked layout, no sticky, no scroll-jacking ─── */}
      <section className="bg-background py-16 md:hidden md:py-24">
        <div className="mx-auto w-full max-w-[1440px] px-4 text-center">
          <h2 className="type-display-md text-balance text-neutral-900">
            {t(keys.stepsHeading)}
          </h2>
          <p className="type-body-lg mx-auto mt-4 max-w-2xl text-balance text-neutral-600">
            {t(keys.stepsSubtitle)}
          </p>
        </div>
        <div className="mx-auto mt-12 flex w-full max-w-[1440px] flex-col gap-16 px-4">
          {PHONE_STEPS.map((step, i) => (
            <div key={i} className="flex flex-col gap-6">
              <div>
                <span className="type-label-md text-aubergine-800">
                  {step.number}
                </span>
                <h3 className="type-headline-sm mt-2 text-neutral-900">
                  {t(keys[step.titleKey])}
                </h3>
                <p className="type-body-md mt-2 leading-relaxed text-neutral-500">
                  {t(keys[step.descKey])}
                </p>
              </div>
              <div className="flex justify-center">
                <PhoneFrame>
                  {i === 0 ? (
                    <SchedulerScreen bloodOnly={bloodOnly} />
                  ) : i === 1 ? (
                    <ConfirmationScreen bloodOnly={bloodOnly} />
                  ) : i === 2 ? (
                    <TimelineScreen />
                  ) : (
                    <CompareScreen />
                  )}
                </PhoneFrame>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Desktop: sticky scroll-linked layout ─── */}
      <section
        ref={sectionRef}
        className="bg-background relative hidden pb-16 md:block md:pb-24"
        style={{ minHeight: `${PHONE_STEPS.length * 100}vh` }}
      >
        <div className="sticky top-0 flex min-h-screen flex-col justify-center py-16 md:py-20">
          {/* Section heading */}
          <div className="mx-auto mb-8 w-full max-w-[1440px] overflow-hidden px-4 text-center md:mb-12 md:px-10">
            <h2 className="type-display-md text-neutral-900">
              {t(keys.stepsHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-2xl text-neutral-600">
              {t(keys.stepsSubtitle)}
            </p>
          </div>

          <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-12 px-4 md:grid-cols-2 md:px-10">
            {/* Phone — screen changes with active step */}
            <div className="flex justify-center">
              {/* Phone screen: renders the current step directly on the
                  server (static, via the ClientOnly fallback) and only
                  mounts AnimatePresence after hydration, so framer-motion's
                  Math.random() never reaches the SSR output and the
                  subtree stays prerendered. */}
              <PhoneFrame>
                {(() => {
                  const screen =
                    activeStep === 0 ? (
                      <SchedulerScreen bloodOnly={bloodOnly} />
                    ) : activeStep === 1 ? (
                      <ConfirmationScreen bloodOnly={bloodOnly} />
                    ) : activeStep === 2 ? (
                      <TimelineScreen />
                    ) : (
                      <CompareScreen />
                    )
                  return (
                    <ClientOnly fallback={screen}>
                      <AnimatePresence mode="wait">
                        <m.div
                          key={activeStep}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                          {screen}
                        </m.div>
                      </AnimatePresence>
                    </ClientOnly>
                  )
                })()}
              </PhoneFrame>
            </div>

            {/* Steps timeline — all steps visible, active one highlighted */}
            <div ref={stepsColumnRef} className="relative pl-10">
              {/* Vertical track line — spans from first dot center to last dot center */}
              <div
                className="absolute left-[11px] w-px rounded-full bg-neutral-200"
                style={{ top: trackBounds.top, height: trackBounds.height }}
              />
              {/* Progress fill — rescaled so the line reaches the last dot when
                you reach the last step's zone (N steps span N-1 gaps) */}
              <div
                className="absolute left-[11px] w-px rounded-full bg-neutral-400"
                style={{
                  top: trackBounds.top,
                  height:
                    trackBounds.height *
                    Math.min(
                      1,
                      (progress * PHONE_STEPS.length) / (PHONE_STEPS.length - 1)
                    ),
                }}
              />

              {PHONE_STEPS.map((step, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    const section = sectionRef.current
                    if (!section) return
                    const scrollRange =
                      section.offsetHeight - window.innerHeight
                    // Target just inside the start of step i's zone so the line
                    // fill ends exactly at step i's dot (not past it). The small
                    // 0.01 offset guards against float precision dropping us into
                    // the previous step's zone.
                    const targetP = (i + 0.01) / PHONE_STEPS.length
                    const sectionTop =
                      section.getBoundingClientRect().top + window.scrollY
                    const targetY = sectionTop + targetP * scrollRange
                    window.scrollTo({ top: targetY, behavior: "smooth" })
                  }}
                  className="group relative mb-10 block w-full cursor-pointer text-left last:mb-0 focus:outline-none"
                  aria-label={`Go to step ${step.number}: ${t(keys[step.titleKey])}`}
                >
                  {/* Step dot — always fully opaque so the timeline line never shows through */}
                  <div
                    ref={(el) => {
                      dotsRef.current[i] = el
                    }}
                    className="type-body-sm absolute top-1.5 -left-10 z-10 flex h-[22px] w-[22px] items-center justify-center rounded-full font-medium"
                    style={{
                      backgroundColor: i <= activeStep ? "#919191" : "#e8e8e8",
                      color: i <= activeStep ? "white" : "#a0a0a0",
                      boxShadow: "0 0 0 3px #F6F6F6",
                      transition: "background-color 0.3s, color 0.3s",
                    }}
                  >
                    {step.number}
                  </div>

                  {/* Text content — fades when step is inactive */}
                  <div
                    className={cn(
                      "transition-opacity duration-500 group-hover:opacity-100",
                      i === activeStep ? "opacity-100" : "opacity-30"
                    )}
                  >
                    <h3 className="type-headline-sm text-neutral-900">
                      {t(keys[step.titleKey])}
                    </h3>
                    <p className="type-body-md mt-2 max-w-md leading-relaxed text-neutral-500">
                      {t(keys[step.descKey])}
                    </p>
                  </div>
                </button>
              ))}

              {/* Scroll hint — hidden on SSR (ClientOnly's null fallback),
                  mounted after hydration so AnimatePresence's Math.random()
                  doesn't escape into the static shell. The hint is an
                  ephemeral "scroll to explore" cue that only appears on
                  step 0 anyway. */}
              <ClientOnly>
                <AnimatePresence>
                  {activeStep === 0 && (
                    <m.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="mt-6 flex items-center gap-2 text-sm text-neutral-400"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="animate-bounce"
                      >
                        <path
                          d="M8 3v10M4 9l4 4 4-4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {ut("scrollToExplore")}
                    </m.p>
                  )}
                </AnimatePresence>
              </ClientOnly>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
